import React from "react";
import { Stack, Button, Tooltip, Chip, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useDispatch } from "react-redux";
import { setAlert } from "../../redux/modules/alert";

const FileUploader = ({
  attachments,
  setAttachments,
  maxFileSizeMB = 10,
  existingFiles = [],
  setDeletedFileIds,
  setExistingFiles,
}) => {
  const dispatch = useDispatch();
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const maxFileSize = maxFileSizeMB * 1024 * 1024;

    // Validate file size
    const invalidFiles = newFiles?.filter((file) => file.size > maxFileSize);
    if (invalidFiles?.length) {
      dispatch(
        setAlert(
          `File "${invalidFiles?.[0].name}" exceeds ${maxFileSizeMB}MB limit. Please choose a smaller file.`,
          "info"
        )
      );
      e.target.value = "";
      return;
    }

    // Merge & remove duplicates
    const combined = [...attachments, ...newFiles];
    const uniqueFiles = combined?.filter(
      (file, index, self) =>
        index ===
        self.findIndex((f) => f.name === file.name && f.size === file.size)
    );
    setAttachments(uniqueFiles);
    e.target.value = "";
  };

  const isEditMode =
    (existingFiles?.length > 0 || typeof setDeletedFileIds === "function") &&
    typeof setExistingFiles === "function";

  const handleRemove = (file, index, isExisting = false) => {
    if (isEditMode && isExisting && file.id) {
      setDeletedFileIds?.((prev) => [...prev, file.id]);
      setExistingFiles?.((prev) => prev.filter((f) => f.id !== file.id));
    } else {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
    }
  };

  return (
    <div>
      <Stack direction="row" alignItems="center" spacing={2}>
        <div>
          <Tooltip title={`Attach files (max size up to ${maxFileSizeMB}MB)`}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              sx={{ height: 40, flexShrink: 0 }}
            >
              Attach Files
              <input
                type="file"
                multiple
                hidden
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,image/*"
              />
            </Button>
          </Tooltip>
        </div>
        {(attachments?.length > 0 || existingFiles?.length > 0) && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexWrap: "nowrap",
              overflowX: "auto",
              overflowY: "hidden",
              "&::-webkit-scrollbar": { height: "5px" },
              padding: 1,
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0,0,0,0.2)",
                borderRadius: "4px",
              },
              width: "70%",
            }}
          >
            {/* Existing files (edit mode) */}
            {isEditMode &&
              existingFiles?.map((file, index) => (
                <Chip
                  key={`existing-${file.id || index}`}
                  icon={<InsertDriveFileIcon />}
                  size="small"
                  label={file.file_name}
                  onDelete={() => handleRemove(file, index, true)}
                  onClick={() => {
                    if (file?.file_path) {
                      const link = document.createElement("a");
                      link.href = file?.file_path;
                      link.target = "_blank";
                      link.rel = "noopener noreferrer";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  sx={{
                    maxWidth: 150,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    flexShrink: 0,
                  }}
                />
              ))}

            {/* New attachments */}
            {attachments?.map((file, index) => (
              <Chip
                key={`new-${index}`}
                variant="outlined"
                size="small"
                icon={<InsertDriveFileIcon />}
                label={file.name}
                onDelete={() => handleRemove(file, index, false)}
                sx={{
                  maxWidth: 150,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              />
            ))}
          </Stack>
        )}
      </Stack>
      <Typography mt={0.5} variant="caption" color="textSecondary">
        Max file size: 10 MB.
        <br /> Accepted formats: PDF, DOC, DOCX, and Images.
      </Typography>
    </div>
  );
};

export default FileUploader;
